import { ChevronLeft, ChevronRight, Info, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { PALETTE, paletteOf } from './meta'
import { ESCALATIONS, nextKey, OPERATORS, RELATIONS } from './model'
import type { RecipientDraft, RecipientKind, StepDraft } from './model'
import type { WorkflowContextField, WorkflowPeopleView } from '../types'

interface StepConfigPanelProps {
  step: StepDraft
  index: number
  count: number
  people: WorkflowPeopleView | undefined
  fields: WorkflowContextField[]
  /** Server problems for this step, by part: name, recipients, conditions, delayMinutes, slaHours. */
  errors: Record<string, string>
  onChange: (step: StepDraft) => void
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return <p id={id} className="mt-1 text-xs text-[var(--color-danger)]">{message}</p>
}

/**
 * The right column: everything about the selected step.
 *
 * <p>What it shows depends on the kind, and what is not shown is not sent — a step changed from
 * approval to delay does not carry an SLA nobody can see any more. The server applies the same
 * rule, so the two cannot disagree about what was saved.
 */
export function StepConfigPanel({
  step, index, count, people, fields, errors, onChange, onPrev, onNext, onClose,
}: StepConfigPanelProps) {
  const meta = PALETTE[paletteOf(step)]
  const Icon = meta.icon
  const set = (patch: Partial<StepDraft>) => onChange({ ...step, ...patch })
  const id = (name: string) => `step-${step.key}-${name}`

  return (
    <Card className="p-3" aria-label="Step Configuration">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">Step Configuration</h3>
          <p className="text-xs text-[var(--color-muted)]">Step {index + 1} of {count}</p>
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" aria-label="Previous step"
            disabled={index === 0} onClick={onPrev}>
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="sm" aria-label="Next step"
            disabled={index === count - 1} onClick={onNext}>
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="sm" aria-label="Close step configuration" onClick={onClose}>
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="mb-3 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2">
        <span className={`flex size-8 shrink-0 items-center justify-center rounded-md ${meta.tint}`}>
          <Icon className={`size-4 ${meta.tone}`} aria-hidden="true" />
        </span>
        <span>
          <span className="block text-xs text-[var(--color-muted)]">Step Type</span>
          <span className="block text-sm font-medium text-[var(--color-heading)]">{meta.label}</span>
          <span className="block text-xs text-[var(--color-muted)]">{meta.hint}</span>
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor={id('name')}>Step Name *</Label>
          <Input
            id={id('name')} value={step.name} maxLength={200}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? id('name-error') : undefined}
            onChange={(event) => set({ name: event.target.value })}
          />
          <FieldError id={id('name-error')} message={errors.name} />
        </div>
        <div>
          <Label htmlFor={id('description')}>Step Description</Label>
          <Textarea
            id={id('description')} rows={2} maxLength={1000} value={step.description}
            placeholder={step.kind === 'NOTIFICATION' ? 'The message people receive' : 'What happens here'}
            onChange={(event) => set({ description: event.target.value })}
          />
        </div>

        {(step.kind === 'APPROVAL' || step.kind === 'NOTIFICATION') && (
          <Recipients
            step={step} people={people} error={errors.recipients} errorId={id('recipients-error')}
            title={step.kind === 'APPROVAL' ? 'Approvers *' : 'Notify *'}
            onChange={(recipients) => set({ recipients })}
          />
        )}

        {step.kind === 'APPROVAL' && (
          <>
            {step.mode !== 'SEQUENTIAL'
              ? (
                <div>
                  <Label htmlFor={id('mode')}>Who must approve</Label>
                  <Select id={id('mode')} className="w-full" value={step.mode}
                    onChange={(event) => set({ mode: event.target.value as StepDraft['mode'] })}>
                    <option value="PARALLEL_ALL">Everyone listed</option>
                    <option value="PARALLEL_ANY">Any one of them</option>
                  </Select>
                </div>
              )
              : step.recipients.length > 1 && (
                <p className="text-xs text-[var(--color-muted)]">
                  Only the first person found is asked. Use Parallel Approval to ask several at once.
                </p>
              )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor={id('sla')}>SLA (hours)</Label>
                <Input
                  id={id('sla')} type="number" min="1" value={step.slaHours} placeholder="None"
                  aria-invalid={errors.slaHours ? true : undefined}
                  onChange={(event) => set({ slaHours: event.target.value })}
                />
                <FieldError id={id('sla-error')} message={errors.slaHours} />
              </div>
              <div>
                <Label htmlFor={id('escalation')}>When overdue</Label>
                <Select id={id('escalation')} className="w-full" value={step.escalationType}
                  onChange={(event) => set({ escalationType: event.target.value })}>
                  {ESCALATIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-[var(--color-body)]">
              <input type="checkbox" className="mt-1" checked={step.allowSkip}
                onChange={(event) => set({ allowSkip: event.target.checked })} />
              <span>Allow skip — when nobody is found, move on instead of stopping</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-[var(--color-body)]">
              <input type="checkbox" className="mt-1" checked={step.allowSelfApproval}
                onChange={(event) => set({ allowSelfApproval: event.target.checked })} />
              <span>Allow self-approval — the requester may approve their own request</span>
            </label>
          </>
        )}

        {step.kind === 'NOTIFICATION' && (
          <fieldset className="space-y-1">
            <legend className="mb-1 text-sm font-medium text-[var(--color-heading)]">Send via</legend>
            <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
              <input type="checkbox" checked disabled /> In-app (always)
            </label>
            <label className="flex items-start gap-2 text-sm text-[var(--color-body)]">
              <input type="checkbox" className="mt-1" checked={step.notifyEmail}
                onChange={(event) => set({ notifyEmail: event.target.checked })} />
              <span>Email — to everyone notified by this step</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
              <input type="checkbox" className="mt-1" disabled />
              <span>SMS — no SMS gateway configured</span>
            </label>
          </fieldset>
        )}

        {step.kind === 'CONDITION' && (
          <ConditionRows step={step} fields={fields} error={errors.conditions}
            errorId={id('conditions-error')} onChange={(conditions) => set({ conditions })} />
        )}

        {step.kind === 'DELAY' && (
          <div>
            <p className="mb-1 text-sm font-medium text-[var(--color-heading)]">Duration *</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor={id('delay')} className="sr-only">How long</Label>
                <Input
                  id={id('delay')} type="number" min="1" value={step.delayAmount}
                  aria-label="How long"
                  aria-invalid={errors.delayMinutes ? true : undefined}
                  aria-describedby={errors.delayMinutes ? id('delay-error') : undefined}
                  onChange={(event) => set({ delayAmount: event.target.value })}
                />
              </div>
              <Select aria-label="Unit" className="w-full" value={step.delayUnit}
                onChange={(event) => set({ delayUnit: event.target.value as StepDraft['delayUnit'] })}>
                <option value="HOURS">Hours</option>
                <option value="DAYS">Days</option>
              </Select>
            </div>
            <FieldError id={id('delay-error')} message={errors.delayMinutes} />
            <p className="mt-1 flex items-start gap-1 text-xs text-[var(--color-muted)]">
              <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              The request waits here, then carries on. A restart does not lose the wait.
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}

function Recipients({ step, people, title, error, errorId, onChange }: {
  step: StepDraft
  people: WorkflowPeopleView | undefined
  title: string
  error?: string
  errorId: string
  onChange: (rows: RecipientDraft[]) => void
}) {
  const rows = step.recipients
  const update = (key: string, patch: Partial<RecipientDraft>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <div>
      <p className="mb-1 text-sm font-medium text-[var(--color-heading)]">{title}</p>
      <ul className="space-y-2">
        {rows.map((row, at) => (
          <li key={row.key} className="flex items-center gap-1">
            <Select aria-label={`Who ${at + 1}: kind`} className="w-32 shrink-0" value={row.kind}
              onChange={(event) => update(row.key, {
                kind: event.target.value as RecipientKind,
                value: event.target.value === 'RELATION' ? 'MANAGER_1' : '',
              })}>
              <option value="RELATION">Record relation</option>
              <option value="ROLE">Role</option>
              <option value="USER">Specific user</option>
            </Select>
            <Select aria-label={`Who ${at + 1}`} className="min-w-0 flex-1" value={row.value}
              onChange={(event) => update(row.key, { value: event.target.value })}>
              {row.kind !== 'RELATION' && <option value="">Choose…</option>}
              {row.kind === 'RELATION' && RELATIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
              {row.kind === 'RELATION' && !RELATIONS.some((option) => option.value === row.value) && (
                <option value={row.value}>{`Manager, ${row.value.slice(8)} levels up`}</option>
              )}
              {row.kind === 'ROLE' && people?.roles.map((role) => (
                <option key={role.id} value={role.id}>{role.label}</option>
              ))}
              {row.kind === 'USER' && people?.users.map((user) => (
                <option key={user.id} value={user.id}>{user.label}</option>
              ))}
            </Select>
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove who ${at + 1}`}
              onClick={() => onChange(rows.filter((other) => other.key !== row.key))}>
              <X className="size-4" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      <Button type="button" variant="outline" size="sm" className="mt-2"
        onClick={() => onChange([...rows, { key: nextKey('r'), kind: 'RELATION', value: 'MANAGER_1' }])}>
        <Plus className="size-4" aria-hidden="true" /> Add
      </Button>
      <FieldError id={errorId} message={error} />
    </div>
  )
}

function ConditionRows({ step, fields, error, errorId, onChange }: {
  step: StepDraft
  fields: WorkflowContextField[]
  error?: string
  errorId: string
  onChange: (rows: StepDraft['conditions']) => void
}) {
  const rows = step.conditions
  const update = (key: string, patch: Partial<StepDraft['conditions'][number]>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <div>
      <p className="mb-1 text-sm font-medium text-[var(--color-heading)]">Only when *</p>
      <ul className="space-y-2">
        {rows.map((row, at) => {
          const needsValue = OPERATORS.find((op) => op.value === row.operator)?.needsValue !== false
          const known = fields.some((field) => field.name === row.field)
          return (
            <li key={row.key} className="space-y-1 rounded-md border border-[var(--color-border)] p-2">
              {fields.length > 0
                ? (
                  <Select aria-label={`Rule ${at + 1}: field`} className="w-full" value={row.field}
                    onChange={(event) => update(row.key, { field: event.target.value })}>
                    <option value="">Choose a field…</option>
                    {fields.map((field) => (
                      <option key={field.name} value={field.name}>{field.label}</option>
                    ))}
                    {row.field && !known && <option value={row.field}>{row.field}</option>}
                  </Select>
                )
                : (
                  <Input aria-label={`Rule ${at + 1}: field`} value={row.field} placeholder="Field, e.g. amount"
                    onChange={(event) => update(row.key, { field: event.target.value })} />
                )}
              <div className="flex items-center gap-1">
                <Select aria-label={`Rule ${at + 1}: operator`} className="w-36 shrink-0" value={row.operator}
                  onChange={(event) => update(row.key, { operator: event.target.value })}>
                  {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                </Select>
                {needsValue && (
                  <Input aria-label={`Rule ${at + 1}: value`} className="min-w-0 flex-1" value={row.value}
                    onChange={(event) => update(row.key, { value: event.target.value })} />
                )}
                <Button type="button" variant="ghost" size="sm" aria-label={`Remove rule ${at + 1}`}
                  onClick={() => onChange(rows.filter((other) => other.key !== row.key))}>
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </div>
            </li>
          )
        })}
      </ul>
      <Button type="button" variant="outline" size="sm" className="mt-2"
        onClick={() => onChange([...rows, { key: nextKey('c'), field: '', operator: 'EQ', value: '' }])}>
        <Plus className="size-4" aria-hidden="true" /> Add rule
      </Button>
      <FieldError id={errorId} message={error} />
      <p className="mt-2 flex items-start gap-1 rounded-md bg-[var(--color-tint-green)] p-2 text-xs text-[var(--color-body)]">
        <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
        Steps below run only when every rule matches — up to the next condition or the end. Otherwise
        they are skipped and the request carries on after them. This is a gate, not a branch.
      </p>
    </div>
  )
}
